import React, { useEffect, useState } from 'react';
import { Plus, FolderOpen, Trash2, X, Loader2, RefreshCw, Edit2 } from 'lucide-react';
import { crmCategoriesApi, CrmCategory } from '../services/crmApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Categories() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [categories, setCategories] = useState<CrmCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CrmCategory | null>(null);
  const [formData, setFormData] = useState({
    Name: '',
    Description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await crmCategoriesApi.list();
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCategories();
  }, []);
  // Filter by team and region scope
  const filteredCategories = categories.filter(c => {
    // Team filter: only show categories belonging to current workspace or with no team
    if (c.Team && c.Team !== currentWorkspace.name && c.Team !== team) return false;
    // Region filter
    if (currentSubRegion) {
      return !c.Region || c.Region === currentSubRegion;
    }
    return true;
  });
  const handleOpenCreate = () => {
    setEditingCategory(null);
    setFormData({
      Name: '',
      Description: ''
    });
    setShowModal(true);
  };
  const handleOpenEdit = (cat: CrmCategory) => {
    setEditingCategory(cat);
    setFormData({
      Name: cat.Name || '',
      Description: cat.Description || ''
    });
    setShowModal(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.Name.trim()) return;
    setIsSubmitting(true);
    try {
      const payload: {
        Name: string;
        Description?: string;
        Team: string;
        Region?: string;
      } = {
        Name: formData.Name.trim(),
        Description: formData.Description.trim() || undefined,
        Team: team
      };
      if (currentSubRegion) {
        payload.Region = currentSubRegion;
      }
      if (editingCategory) {
        await crmCategoriesApi.update(editingCategory.id, payload);
        toast.success('Category updated successfully');
      } else {
        await crmCategoriesApi.create(payload);
        toast.success('Category created successfully');
      }
      setShowModal(false);
      fetchCategories();
    } catch (err) {
      console.error('Failed to save category:', err);
      toast.error(editingCategory ? 'Failed to update category' : 'Failed to create category');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await crmCategoriesApi.delete(id);
      toast.success('Category deleted successfully');
      setDeleteConfirm(null);
      fetchCategories();
    } catch (err) {
      console.error('Failed to delete category:', err);
      toast.error('Failed to delete category');
    } finally {
      setIsDeleting(false);
    }
  };
  return <div className="space-y-6" data-id="element-1960">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1961">
        <div className="min-w-0" data-id="element-1962">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1963">Categories</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1964">
            Manage contact categories (synced with Zoho CRM).
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1965">
          <button onClick={fetchCategories} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-1966">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-1967" />
          </button>
          <button onClick={handleOpenCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-1968">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-1969" />
            <span data-id="element-1970">Create Category</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-1971">
        {loading && filteredCategories.length === 0 ? <div className="p-12 text-center" data-id="element-1972">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-1973" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-1974">
              Loading categories from CRM...
            </p>
          </div> : filteredCategories.length === 0 ? <div className="p-12 text-center" data-id="element-1975">
            <FolderOpen className="mx-auto h-12 w-12 text-gray-400" data-id="element-1976" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-1977">
              No categories found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1978">
              {currentSubRegion ? `No categories found for region "${currentSubRegion}".` : 'Create categories to organize your contacts.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-1979">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1980">
              <thead className="bg-gray-50" data-id="element-1981">
                <tr data-id="element-1982">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1983">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1984">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1985">
                    Team
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1986">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1987">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1988">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1989">
                {filteredCategories.map(cat => <tr key={cat.id} className="hover:bg-gray-50" data-id="element-1990">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1991">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200" data-id="element-1992">
                        <FolderOpen className="h-3.5 w-3.5 flex-shrink-0" data-id="element-1993" />
                        {cat.Name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" data-id="element-1994">
                      {cat.Description || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1995">
                      {cat.Team || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1996">
                      {cat.Region || 'All Regions'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1997">
                      {cat.Created_Time ? new Date(cat.Created_Time).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1998">
                      <div className="flex items-center justify-end gap-2" data-id="element-1999">
                        <button onClick={() => handleOpenEdit(cat)} className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded" title="Edit" data-id="element-2000">
                          <Edit2 className="h-4 w-4" data-id="element-2001" />
                        </button>
                        <button onClick={() => setDeleteConfirm(cat.id)} className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-2002">
                          <Trash2 className="h-4 w-4" data-id="element-2003" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Create/Edit Modal */}
      {showModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2004">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-2005">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} data-id="element-2006" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-2007">
              <div className="flex items-center justify-between mb-4" data-id="element-2008">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2009">
                  {editingCategory ? 'Edit Category' : 'Create New Category'}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-2010">
                  <X className="h-5 w-5" data-id="element-2011" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2012">
                <div data-id="element-2013">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2014">
                    Category Name
                  </label>
                  <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Name} onChange={e => setFormData({
                ...formData,
                Name: e.target.value
              })} placeholder="e.g., Influencer, Partner, Sponsor" data-id="element-2015" />
                </div>
                <div data-id="element-2016">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2017">
                    Description (Optional)
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.Description} onChange={e => setFormData({
                ...formData,
                Description: e.target.value
              })} placeholder="What is this category used for?" data-id="element-2018" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-2019">
                  <div data-id="element-2020">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2021">
                      Team
                    </label>
                    <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-2022" />
                  </div>
                  <div data-id="element-2023">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2024">
                      Region
                    </label>
                    <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={currentSubRegion || 'All Regions'} disabled data-id="element-2025" />
                  </div>
                </div>
                <p className="text-xs text-gray-400" data-id="element-2026">
                  Team and Region are set from your current scope
                </p>
                <div className="flex justify-end gap-3 pt-4" data-id="element-2027">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2028">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting || !formData.Name.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-id="element-2029">
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-2030" />}
                    {editingCategory ? 'Update Category' : 'Create Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2031">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-2032">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-2033" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-2034">
              <div className="flex items-center gap-4 mb-4" data-id="element-2035">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-2036">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-2037" />
                </div>
                <div data-id="element-2038">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2039">
                    Delete Category
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-2040">
                    Are you sure? This will remove the category from CRM.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-2041">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2042">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-2043">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-2044" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}